import {
  SEEKA_TRACKING_TYPE,
  SeekaDataPreferences,
  SeekaTrackingBeacon,
  TrackingBeacons,
} from '../useTrackingBeacons/transform';

// Mirrors the shape returned by /api/[eventId]/seeka-profile. Duplicated here
// (rather than imported from the API route) to keep server-only code out of the
// client bundle, matching how useUserData duplicates the /me response type.
type SeekaProfileResponse = {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
};

type GryllsSeekaModule = {
  seekaIdentify?: (profile: Record<string, string>) => void;
};

/**
 * Resolves the per-field consent flags across the Seeka beacon(s) on this event.
 *
 * `seekaIdentify` triggers a single global `Converge.identity.mergeProfile`
 * applied to every configured Seeka instance at once — it can't gate
 * per-instance. So a field is sent only if EVERY beacon enables it
 * (intersection). With the privacy-safe default (a field is enabled only by an
 * explicit `true`), an event with no Seeka beacons sends nothing.
 */
export const getSeekaDataPreferences = (trackingBeacons: TrackingBeacons | null): SeekaDataPreferences => {
  const beacons = (trackingBeacons?.[SEEKA_TRACKING_TYPE] ?? []) as SeekaTrackingBeacon[];

  const enabledForEveryBeacon = (field: keyof SeekaDataPreferences) =>
    beacons.length > 0 && beacons.every((beacon) => beacon?.dataPreferences?.[field] === true);

  return {
    firstName: enabledForEveryBeacon('firstName'),
    lastName: enabledForEveryBeacon('lastName'),
    email: enabledForEveryBeacon('email'),
    phone: enabledForEveryBeacon('phone'),
  };
};

/**
 * Builds the Seeka mergeProfile payload, keeping only fields the organizer
 * enabled AND that actually carry a value.
 */
export const buildGatedSeekaProfile = (
  prefs: SeekaDataPreferences,
  fields: { email?: string | null; firstName?: string | null; lastName?: string | null; phone?: string | null },
): Record<string, string> => {
  const profile: Record<string, string> = {};
  if (prefs.email && fields.email) {
    profile.email = String(fields.email);
  }
  if (prefs.firstName && fields.firstName) {
    profile.firstName = String(fields.firstName);
  }
  if (prefs.lastName && fields.lastName) {
    profile.lastName = String(fields.lastName);
  }
  if (prefs.phone && fields.phone) {
    profile.phone = String(fields.phone);
  }
  return profile;
};

/**
 * Safe wrapper for grylls.seekaIdentify() — fires a Seeka-only mergeProfile.
 * No-ops when grylls/Seeka is unavailable (e.g. an older grylls without the
 * method) or the profile is empty, so we never send an empty identity.
 */
export const safeGryllsSeekaIdentify = (grylls: GryllsSeekaModule, profile: Record<string, string>) => {
  try {
    if (!grylls || typeof grylls.seekaIdentify !== 'function') {
      // Older grylls without seekaIdentify — nothing to do, not an error.
      return;
    }
    if (!profile || Object.keys(profile).length === 0) {
      return;
    }
    grylls.seekaIdentify(profile);
  } catch (error) {
    console.error('Seeka mergeProfile: grylls.seekaIdentify failed', { error });
  }
};

const fetchSeekaProfile = async (eventId: string): Promise<SeekaProfileResponse | null> => {
  try {
    const response = await fetch(`/e/api/${eventId}/seeka-profile`, { method: 'GET' });
    if (!response.ok) return null;
    return (await response.json()) as SeekaProfileResponse;
  } catch {
    return null;
  }
};

/**
 * Triggers a Seeka identity merge (Converge `mergeProfile`) for an
 * authenticated user landing on the event page.
 *
 * Caller must have already confirmed the user is authenticated. The merge is
 * best-effort and Seeka-scoped: it only fetches PII and calls grylls once the
 * organizer has opted at least one resolvable field in, and never throws.
 *
 * `isCancelled` lets the caller abort a stale run: this function awaits a
 * network fetch and a dynamic import, during which the user may navigate to
 * another event (re-running grylls.load with that event's Seeka instances) or
 * log out. Without the guard the in-flight call would merge this event's
 * profile/consent into whatever Seeka instances are now loaded. It's re-checked
 * immediately before the merge so a cancelled run never calls seekaIdentify.
 */
export const seekaMergeProfile = async ({
  eventId,
  trackingBeacons,
  isCancelled = () => false,
}: {
  eventId: string;
  trackingBeacons: TrackingBeacons | null;
  isCancelled?: () => boolean;
}): Promise<void> => {
  const prefs = getSeekaDataPreferences(trackingBeacons);

  // Phone isn't available on the lander (no checkout form, not returned by the
  // profile endpoint), so skip the network call unless a field we can actually
  // provide is enabled.
  if (!prefs.email && !prefs.firstName && !prefs.lastName) {
    return;
  }

  const profileFields = await fetchSeekaProfile(eventId);
  if (!profileFields || isCancelled()) {
    return;
  }

  const profile = buildGatedSeekaProfile(prefs, profileFields);
  if (Object.keys(profile).length === 0) {
    return;
  }

  let grylls: GryllsSeekaModule;
  try {
    const mod = await import('@eventbrite/legacy-infra.grylls');
    grylls = (mod.default ?? mod) as GryllsSeekaModule;
  } catch {
    // Chunk load failure (e.g. network blip / deploy mid-session). Best-effort:
    // give up silently rather than surface an unhandled rejection on the lander.
    return;
  }
  // Re-check after both awaits: the page/event/auth may have changed.
  if (isCancelled()) {
    return;
  }
  safeGryllsSeekaIdentify(grylls, profile);
};
